import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { localYmd, normalizeRange } from './periodUtils'

const WEEKDAYS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс']

function parseYmd(s: string): Date | null {
	if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return null
	const [y, m, d] = s.split('-').map(Number)
	const dt = new Date(y, m - 1, d)
	if (dt.getFullYear() !== y || dt.getMonth() !== m - 1 || dt.getDate() !== d) return null
	return dt
}

function ymdKey(d: Date): string {
	return localYmd(d)
}

function startOfMonth(d: Date): Date {
	return new Date(d.getFullYear(), d.getMonth(), 1)
}

function addMonths(d: Date, delta: number): Date {
	return new Date(d.getFullYear(), d.getMonth() + delta, 1)
}

function monthLabelRu(d: Date): string {
	return d.toLocaleDateString('ru-RU', { month: 'long', year: 'numeric' })
}

function buildMonthCells(anchorMonth: Date): { date: Date; inMonth: boolean }[] {
	const first = startOfMonth(anchorMonth)
	const firstDow = first.getDay()
	const mondayBased = (firstDow + 6) % 7
	const gridStart = new Date(first)
	gridStart.setDate(first.getDate() - mondayBased)
	const cells: { date: Date; inMonth: boolean }[] = []
	for (let i = 0; i < 42; i++) {
		const d = new Date(gridStart)
		d.setDate(gridStart.getDate() + i)
		cells.push({
			date: d,
			inMonth: d.getMonth() === anchorMonth.getMonth(),
		})
	}
	return cells
}

type Props = {
	dateFrom: string
	dateTo: string
	onChange: (from: string, to: string) => void
	id?: string
	label?: string
}

type PopoverCoords = { top: number; left: number; width: number }

export function AuditPeriodPicker({ dateFrom, dateTo, onChange, id, label = 'Период' }: Props) {
	const rootRef = useRef<HTMLDivElement | null>(null)
	const triggerRef = useRef<HTMLButtonElement | null>(null)
	const popoverRef = useRef<HTMLDivElement | null>(null)
	const [open, setOpen] = useState(false)
	const [coords, setCoords] = useState<PopoverCoords>({ top: 0, left: 0, width: 360 })

	const { from, to } = useMemo(() => normalizeRange(dateFrom, dateTo), [dateFrom, dateTo])
	const fromDt = from ? parseYmd(from) : null
	const toDt = to ? parseYmd(to) : null

	const [viewMonth, setViewMonth] = useState<Date>(() => startOfMonth(fromDt ?? toDt ?? new Date()))
	const [draftStart, setDraftStart] = useState<string | null>(null)
	const prevOpenRef = useRef(false)

	useEffect(() => {
		const wasOpen = prevOpenRef.current
		prevOpenRef.current = open
		if (!open) return
		if (wasOpen) return
		const { from: f, to: t } = normalizeRange(dateFrom, dateTo)
		const base = (f && parseYmd(f)) || (t && parseYmd(t)) || new Date()
		setViewMonth(startOfMonth(base))
		setDraftStart(null)
	}, [open, dateFrom, dateTo])

	const updatePopoverPosition = useCallback(() => {
		const tr = triggerRef.current
		if (!tr) return
		const rect = tr.getBoundingClientRect()
		const margin = 8
		const w = Math.min(360, window.innerWidth - 2 * margin)
		let left = rect.left
		if (left + w > window.innerWidth - margin) {
			left = Math.max(margin, window.innerWidth - w - margin)
		}
		if (left < margin) {
			left = margin
		}
		let top = rect.bottom + margin
		const pop = popoverRef.current
		if (pop) {
			const ph = pop.getBoundingClientRect().height
			if (top + ph > window.innerHeight - margin) {
				const above = rect.top - ph - margin
				if (above >= margin) {
					top = above
				}
			}
		}
		setCoords({ top, left, width: w })
	}, [])

	useLayoutEffect(() => {
		if (!open) return
		updatePopoverPosition()
		const idRaf = requestAnimationFrame(() => {
			updatePopoverPosition()
		})
		return () => cancelAnimationFrame(idRaf)
	}, [open, updatePopoverPosition, viewMonth])

	useEffect(() => {
		if (!open) return
		const onScrollOrResize = () => updatePopoverPosition()
		window.addEventListener('resize', onScrollOrResize)
		window.addEventListener('scroll', onScrollOrResize, true)
		return () => {
			window.removeEventListener('resize', onScrollOrResize)
			window.removeEventListener('scroll', onScrollOrResize, true)
		}
	}, [open, updatePopoverPosition])

	useEffect(() => {
		if (!open) return
		const onDoc = (e: MouseEvent) => {
			const t = e.target
			if (!(t instanceof Node)) return
			if (rootRef.current?.contains(t)) return
			if (popoverRef.current?.contains(t)) return
			setOpen(false)
		}
		document.addEventListener('mousedown', onDoc)
		return () => document.removeEventListener('mousedown', onDoc)
	}, [open])

	const cells = useMemo(() => buildMonthCells(viewMonth), [viewMonth])

	const summary = useMemo(() => {
		if (!from || !to) return 'Выберите период'
		const a = parseYmd(from)
		const b = parseYmd(to)
		if (!a || !b) return 'Выберите период'
		const opts: Intl.DateTimeFormatOptions = { day: '2-digit', month: 'short', year: 'numeric' }
		return `${a.toLocaleDateString('ru-RU', opts)} — ${b.toLocaleDateString('ru-RU', opts)}`
	}, [from, to])

	const handleDayClick = (d: Date) => {
		const key = ymdKey(d)
		if (!draftStart) {
			setDraftStart(key)
			onChange(key, key)
			return
		}
		const norm = normalizeRange(draftStart, key)
		onChange(norm.from, norm.to)
		setDraftStart(null)
	}

	const inRange = (d: Date): boolean => {
		if (!from || !to) return false
		const k = ymdKey(d)
		return k >= from && k <= to
	}

	const isEndpoint = (d: Date): boolean => {
		const k = ymdKey(d)
		return (!!from && k === from) || (!!to && k === to)
	}

	return (
		<div ref={rootRef} className="audit-period-root">
			<label className="audit-period-label" htmlFor={id}>
				{label}
			</label>
			<button
				ref={triggerRef}
				id={id}
				type="button"
				className="audit-period-trigger"
				onClick={() => setOpen((v) => !v)}
				aria-haspopup="dialog"
				aria-expanded={open}
			>
				<span className="audit-period-trigger-text">{summary}</span>
				<span className="audit-period-trigger-caret">▾</span>
			</button>

			{open &&
				createPortal(
					<div
						ref={popoverRef}
						className="audit-period-popover audit-period-popover-portal"
						style={{ top: coords.top, left: coords.left, width: coords.width }}
						role="dialog"
						aria-label="Выбор периода"
					>
						<div className="audit-period-nav">
							<button type="button" className="audit-period-nav-btn" onClick={() => setViewMonth((m) => addMonths(m, -1))}>
								‹
							</button>
							<div className="audit-period-month-title">{monthLabelRu(viewMonth)}</div>
							<button type="button" className="audit-period-nav-btn" onClick={() => setViewMonth((m) => addMonths(m, 1))}>
								›
							</button>
						</div>

						<div className="audit-period-weekdays">
							{WEEKDAYS.map((w) => (
								<div key={w}>{w}</div>
							))}
						</div>

						<div className="audit-period-grid">
							{cells.map(({ date, inMonth }) => {
								const k = ymdKey(date)
								const range = inRange(date)
								const endpoint = isEndpoint(date)
								const muted = !inMonth
								return (
									<button
										key={k + (inMonth ? '' : '-o')}
										type="button"
										onClick={() => handleDayClick(date)}
										className={[
											'audit-period-day',
											muted ? 'is-muted' : '',
											range && !endpoint ? 'in-range' : '',
											endpoint ? 'is-endpoint' : '',
										]
											.filter(Boolean)
											.join(' ')}
									>
										{date.getDate()}
									</button>
								)
							})}
						</div>

						<p className="audit-period-hint muted">Первый клик — начало периода, второй — конец.</p>
					</div>,
					document.body,
				)}
		</div>
	)
}
